import { useState } from 'react';
import type { Attachment } from '@rundown/types';
import { Modal } from '../../components';
import { cx } from '../../lib/cx';
import { formatBytes } from './optimiseImage';
import type { PendingUpload } from './useUploader';

/**
 * How files are shown, both while uploading and once attached.
 *
 * Images render as thumbnails and everything else as a labelled chip, because
 * the two answer different questions: for a screenshot the content *is* the
 * information, and for a PDF the filename is.
 */

export function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

/** Files still on their way up. Shown next to the composer they were dropped on. */
export function PendingStrip({
  uploads,
  onRemove,
}: {
  uploads: readonly PendingUpload[];
  onRemove: (localId: string) => void;
}) {
  if (uploads.length === 0) return null;

  return (
    <div className="mt-2 flex flex-wrap gap-2">
      {uploads.map((upload) => (
        <div
          key={upload.localId}
          className={cx(
            'relative flex items-center gap-2 rounded-[8px] border py-1.5 pr-2.5 pl-1.5 fs-11',
            upload.status === 'error' ? 'border-danger-border bg-danger-tint' : 'border-line',
          )}
        >
          {upload.previewUrl !== undefined ? (
            <img src={upload.previewUrl} alt="" className="size-8 flex-none rounded-[5px] object-cover" />
          ) : (
            <span className="grid size-8 flex-none place-items-center rounded-[5px] bg-surface-3 fs-9 font-bold text-muted">
              {extensionLabel(upload.filename)}
            </span>
          )}

          <span className="min-w-0">
            <span className="block max-w-[10.625rem] truncate font-medium text-ink">{upload.filename}</span>
            <span className={cx('block', upload.status === 'error' ? 'text-danger' : 'text-muted')}>
              {upload.status === 'optimising' && 'Optimising…'}
              {upload.status === 'uploading' && `${Math.round(upload.progress * 100)}%`}
              {upload.status === 'error' && upload.error}
              {upload.status === 'done' && (
                <>
                  {formatBytes(upload.byteSize)}
                  {/* Only shown when the browser actually saved something, so
                      it reads as a fact rather than as marketing. */}
                  {upload.savedBytes !== undefined && upload.savedBytes > 0 && (
                    <span className="text-success"> · saved {formatBytes(upload.savedBytes)}</span>
                  )}
                </>
              )}
            </span>
          </span>

          <button
            type="button"
            onClick={() => onRemove(upload.localId)}
            aria-label={`Remove ${upload.filename}`}
            className="ml-1 flex-none rounded px-1 fs-13 leading-none text-faint hover:text-ink"
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

/** Files already attached to a request or a message. */
export function AttachmentStrip({
  attachments,
  className,
}: {
  attachments: readonly Attachment[];
  className?: string;
}) {
  const [lightbox, setLightbox] = useState<Attachment | null>(null);
  if (attachments.length === 0) return null;

  const images = attachments.filter((attachment) => isImage(attachment.contentType));
  const others = attachments.filter((attachment) => !isImage(attachment.contentType));

  return (
    <div className={cx('flex flex-col gap-2', className)}>
      {images.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {images.map((attachment) => (
            <button
              key={attachment.id}
              type="button"
              onClick={() => setLightbox(attachment)}
              className="overflow-hidden rounded-[8px] border border-line hover:border-line-stronger"
            >
              <img
                src={attachment.url}
                alt={attachment.filename}
                loading="lazy"
                className="block max-h-[10.5rem] max-w-[16.25rem] object-cover"
              />
            </button>
          ))}
        </div>
      )}

      {others.map((attachment) => (
        <a
          key={attachment.id}
          href={attachment.url}
          target="_blank"
          rel="noreferrer"
          className="flex w-fit items-center gap-2 rounded-[8px] border border-line py-1.5 pr-3 pl-1.5 fs-11 hover:bg-surface"
        >
          <span className="grid size-8 flex-none place-items-center rounded-[5px] bg-surface-3 fs-9 font-bold text-muted">
            {extensionLabel(attachment.filename)}
          </span>
          <span className="min-w-0">
            <span className="block max-w-[13.75rem] truncate font-medium text-ink">
              {attachment.filename}
            </span>
            <span className="block text-muted">{formatBytes(attachment.byteSize)}</span>
          </span>
        </a>
      ))}

      {/* A Modal, not a bare div.
          It was `role="presentation"` with an onClick, which meant: no Escape,
          no focus trap, no way out at all for anyone not using a mouse. "One
          image, one dismiss" is true and is exactly what a dialog primitive is
          for — the reason not to reach for one was that it looked like
          ceremony, and the cost was a viewer a keyboard user could not close.
          The signed URL is already in the list, so opening still costs no
          request. */}
      <Modal
        open={lightbox !== null}
        onOpenChange={(next) => {
          if (!next) setLightbox(null);
        }}
        title={lightbox?.filename ?? ''}
        className="max-w-[min(56rem,calc(100vw-2rem))]"
      >
        {lightbox !== null && (
          <img
            src={lightbox.url}
            alt={lightbox.filename}
            className="mx-auto max-h-[70vh] max-w-full rounded-lg object-contain"
          />
        )}
      </Modal>
    </div>
  );
}

function extensionLabel(filename: string): string {
  const match = /\.([A-Za-z0-9]{1,8})$/.exec(filename);
  return (match?.[1] ?? 'file').toUpperCase().slice(0, 4);
}
